import * as React from 'react';
import { useLocation } from '@reach/router';
import { rgba } from 'polished';
import styled from 'styled-components';

import { ArrowRightIcon } from '../../icons/Icons';
import { navigate } from '../../utils';
import { Heading, Text } from '../design-system';

const Top = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  transition: transform ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  svg {
    margin-bottom: 10px;
  }
`;

const Bottom = styled.div`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  opacity: 0.4;

  span {
    font-family: ${({ theme }) => theme.fontFamily.MONO};
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    font-weight: ${({ theme }) => theme.fontWeight.BOLD};
    text-transform: uppercase;
    line-height: 11px;
    opacity: 0;
    transition: opacity ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  padding: 30px;
  min-width: 0;
  gap: 50px;
  cursor: pointer;
  border-right: 1px solid ${({ theme }) => theme.borderColor.MUTED};

  transition: box-shadow ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};

  &:last-of-type {
    border-right: none;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.EXTRA_LARGE}) {
    &:nth-child(1),
    &:nth-child(2) {
      border-bottom: 1px solid ${({ theme }) => theme.borderColor.MUTED};
    }

    &:nth-child(2),
    &:nth-child(4) {
      border-right: none;
    }
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    border-bottom: 1px solid ${({ theme }) => theme.borderColor.MUTED};
    border-right: none;

    &:last-of-type {
      border-bottom: none;
    }
  }

  &:hover {
    box-shadow:
      0 0 0 1px ${({ theme }) => rgba(theme.highlightColor, 0.3)},
      0px 4px 50px 0px rgba(0, 0, 0, 0.52);

    ${Top} {
      transform: translateY(-2px);
    }

    ${Bottom} {
      opacity: 1;

      span {
        opacity: 1;
      }
    }
  }
`;

export interface CustomerStoryItemType {
  description: string;
  link: string;
  logo: JSX.Element;
  title: string;
}

export const CustomerStoryItem: React.ComponentType<CustomerStoryItemType> =
  React.memo(({ description, title, link, logo }) => {
    const { search } = useLocation();

    return (
      <Wrapper
        onClick={() => {
          navigate(link, search);
        }}>
        <Top>
          {logo}
          <Heading renderAs="h4">{title}</Heading>
          <Text size="medium">{description}</Text>
        </Top>
        <Bottom>
          <span>Read more</span>
          <ArrowRightIcon />
        </Bottom>
      </Wrapper>
    );
  });
